import { Children, Fragment, cloneElement, isValidElement, type ReactNode } from 'react';

/**
 * Polska typografia ekranów stanu: jednoliterowy spójnik albo przyimek
 * (a, i, o, u, w, z — także wielką literą) nie zostaje na końcu wiersza.
 * Spacja PO nim staje się twardą (U+00A0), więc wyraz przechodzi do
 * następnego wiersza razem ze słowem, które wprowadza. Pauza nie zaczyna
 * wiersza (niżej).
 *
 * `text-wrap: pretty` tego nie załatwia i nie ma jak: Chromium wyrównuje nim
 * wyłącznie długość ostatnich wierszy akapitu, a o tym, że „w” na końcu
 * wiersza jest błędem, nie wie nic (zmierzone: „…wymaga zalogowania w /
 * Insolitum Universe.” na desktopie przy `pretty` w arkuszu).
 *
 * Pauza i półpauza nie otwierają wiersza. „—” (U+2014) i „–” (U+2013)
 * użyte jako pauza — otoczone spacjami — w polskim składzie zostają na końcu
 * wiersza z wyrazem przed sobą, nigdy na początku następnego. Spacja PRZED
 * nimi staje się więc twardą (przegląd 06.10.2026: hero logowania łamało się
 * „…dowodzenia | — w jednej, żywej platformie”, Genesis „…firmę | — Jan-Dar.”).
 * Łączniki w wyrazach i zakresach („Jan-Dar”, „2024–2025”) nie mają spacji
 * po żadnej stronie, więc reguła ich nie dotyka; pauza na początku napisu
 * (bez wyrazu przed sobą) też nie.
 *
 * Bez lookbehind w wyrażeniu: WebView tablicy TV i Safari < 16.4 odrzucają
 * go już przy parsowaniu, czyli razem z całym pakietem. Łańcuch wyrazów
 * jednoliterowych („i w domu”) domyka pętla: po pierwszym przejściu drugi
 * wyraz ma przed sobą twardą spację, która też jest granicą (`\s` ją łapie).
 */
export const TWARDA_SPACJA = '\u00A0';

const WYRAZ_JEDNOLITEROWY = /(^|[\s([{„"'«])([aiouwz]) /gi;

/**
 * Zwykłe odstępy przed pauzą albo półpauzą, po której stoi odstęp (także
 * twardy) albo koniec napisu. Twarda spacja przed pauzą do klasy nie należy,
 * więc reguła jest idempotentna.
 */
const ODSTEP_PRZED_PAUZA = /[ \t\r\n]+([\u2014\u2013])(?=\s|$)/g;

/**
 * Tekst z twardą spacją po każdym jednoliterowym spójniku i przyimku oraz
 * przed każdą pauzą i półpauzą otoczoną spacjami.
 */
export function bezSierotek(tekst: string): string {
  let wynik = tekst.replace(ODSTEP_PRZED_PAUZA, `${TWARDA_SPACJA}$1`);
  for (;;) {
    const nastepny = wynik.replace(WYRAZ_JEDNOLITEROWY, `$1$2${TWARDA_SPACJA}`);
    if (nastepny === wynik) return wynik;
    wynik = nastepny;
  }
}

/** Zawartość, w której spacja jest częścią danych, a nie składu. */
const BEZ_ZMIAN = new Set(['code', 'pre', 'kbd', 'samp']);

/**
 * `bezSierotek` dla treści ekranu podanej jako węzeł Reacta. Ekrany podają
 * treść jako `<p>…</p>`, nie napis (21 z 21 wywołań `EkranBledu` z własną
 * treścią na 05.10.2026, także odmowa braku sesji we wszystkich organach),
 * więc mechanizm tylko dla napisów omijałby każde zdanie, które przegląd
 * znalazł.
 *
 * Schodzi wyłącznie w elementy HTML i fragmenty i zmienia wyłącznie napisy:
 * struktury, atrybutów ani komponentów nie dotyka (komponent może
 * renderować cokolwiek — jego wnętrza stąd nie widać, więc zostaje, jak
 * przyszedł). `code`, `pre`, `kbd` i `samp` też zostają — skopiowany kod
 * z twardą spacją przestaje działać.
 */
export function bezSierotekWTresci(wezel: ReactNode): ReactNode {
  if (typeof wezel === 'string') return bezSierotek(wezel);
  if (Array.isArray(wezel)) return Children.map(wezel, (dziecko) => bezSierotekWTresci(dziecko));
  if (!isValidElement<{ children?: ReactNode }>(wezel)) return wezel;
  const html = typeof wezel.type === 'string';
  if (!(html || wezel.type === Fragment) || (html && BEZ_ZMIAN.has(wezel.type as string))) {
    return wezel;
  }
  const dzieci = wezel.props.children;
  if (dzieci === undefined || dzieci === null) return wezel;
  return cloneElement(wezel, { children: bezSierotekWTresci(dzieci) });
}
